import React, { useCallback, useState } from 'react';
import { Col, Layout, Skeleton, Typography } from 'antd';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { HomeExplorerModal, HomeExplorerPageHeader, HomeExplorerTable } from '../../organisms/home';
import { API_URLS } from '../../../constants/urls/api-urls';
import { HomeWidgetsRecord, getHomeWidgetsRepository } from '../../../repository/mytm/home/homeWidgetsRepository';
import { HomeGroupKey, HomeWidgetStatus } from '../../../types/home';
import { HOME } from '../../../constants/define/translation';
import { appStyles } from '../../../styles';
import CountrySelection from '../../molecules/country-selection/CountrySelection';

const { Content } = Layout;

export const VerticalTiles = () => {
  const { t } = useTranslation(HOME);
  const [loading] = useState(false);
  const [isOpenHomeExplorerModal, setIsOpenHomeExplorerModal] = useState(false);
  const [currentRecord, setCurrentRecord] = useState<HomeWidgetsRecord>();
  const [countrySelected, setCountrySelected] = useState('USA');

  const groupKeyHomeExplorer = HomeGroupKey.HOME_EXPLORER;
  const { data, isValidating, error, mutate } = useSWR(
    [API_URLS.HOME_WIDGETS, groupKeyHomeExplorer, countrySelected],
    (url: string, groupKey) => getHomeWidgetsRepository({ groupKey, country: countrySelected }),
    {
      revalidateOnFocus: false,
      refreshInterval: 600000,
    },
  );

  const onOpenHomeExplorerModal = useCallback(() => {
    setIsOpenHomeExplorerModal(true);
  }, []);

  const onCloseHomeExplorerModal = useCallback(() => {
    setIsOpenHomeExplorerModal(false);
    setCurrentRecord(undefined);
  }, []);

  const onHandleClickBtnEdit = useCallback((record: HomeWidgetsRecord) => {
    setCurrentRecord(record);
    setIsOpenHomeExplorerModal(true);
  }, []);

  const extraDropdown = (
    <CountrySelection key="CountrySelection" onCountryChange={(item: string) => setCountrySelected(item)} />
  );

  if (!data || error) {
    return (
      <Container>
        <Content>
          <HomeExplorerPageHeader onOpenHomeExplorerModal={onOpenHomeExplorerModal} extraDropdown={extraDropdown} />
          <Skeleton active />
        </Content>
      </Container>
    );
  }

  const recordHomeExplorerActive = data.filter(
    item => item.groupKey === HomeGroupKey.HOME_EXPLORER && item.status === HomeWidgetStatus.ACTIVE,
  );

  const recordHomeExplorerDraft = data
    .filter(item => item.groupKey === HomeGroupKey.HOME_EXPLORER && item.status === HomeWidgetStatus.INACTIVE)
    .sort((a: any, b: any) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    })
    .map((item: any, index: number) => ({ ...item, ...{ index } }));

  const recordHomeExplorerClosed = data
    .filter(item => item.groupKey === HomeGroupKey.HOME_EXPLORER && item.status === HomeWidgetStatus.CLOSED)
    .sort((a: any, b: any) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });

  const recordHomeExplorer = [...recordHomeExplorerActive, ...recordHomeExplorerClosed].map(
    (item: any, index: number) => ({
      ...item,
      ...{ index },
    }),
  );

  return (
    <>
      <Container>
        <Content>
          <HomeExplorerPageHeader onOpenHomeExplorerModal={onOpenHomeExplorerModal} extraDropdown={extraDropdown} />
          <Col span={14}>
            <HomeExplorerTable
              recordsActive={recordHomeExplorerActive}
              records={recordHomeExplorer}
              loading={loading || isValidating}
              onRefresh={mutate}
              onHandleClickBtnEdit={onHandleClickBtnEdit}
              isActiveList
            />
          </Col>
          {recordHomeExplorerDraft.length > 0 && (
            <Col span={14} className="drafts-list">
              <Typography.Title level={4}>{t('draftsList')}</Typography.Title>
              <HomeExplorerTable
                records={recordHomeExplorerDraft}
                loading={loading || isValidating}
                onRefresh={mutate}
                onHandleClickBtnEdit={onHandleClickBtnEdit}
                recordsActive={recordHomeExplorerActive}
              />
            </Col>
          )}
          <HomeExplorerModal
            isOpenModal={isOpenHomeExplorerModal}
            onClose={onCloseHomeExplorerModal}
            onRefresh={mutate}
            record={currentRecord}
          />
        </Content>
      </Container>
    </>
  );
};

const Container = styled.div`
  ${appStyles.paddingComponent}

  & .drafts-list,
  & .closed-list {
    margin-top: 60px;
  }
`;
